<!--
 
 Copyright © 2025-2026 Ping Identity Corporation. All right reserved.
 
 This software may be modified and distributed under the terms
 of the MIT license. See the LICENSE file for details.
 
 -->

<script lang="ts">
  import T from '$components/_utilities/locale-strings.svelte';
  import Button from '$components/primitives/button/button.svelte';
  import Grid from '$components/primitives/grid/grid.svelte';
  import AppleIcon from '../../../components/icons/apple-icon.svelte';
  import FacebookIcon from '../../../components/icons/facebook-icon.svelte';
  import GoogleIcon from '../../../components/icons/google-icon.svelte';
  import LinkedinIcon from '../../../components/icons/linkedin-icon.svelte';
  import MicrosoftIcon from '../../../components/icons/microsoft-icon.svelte';

  import type { SelectIdPCallback } from '@forgerock/journey-client/types';
  import type { z } from 'zod';

  import type { Maybe } from '$core/interfaces';
  import type { styleSchema } from '$core/style.store';
  import type {
    CallbackMetadata,
    SelfSubmitFunction,
    StepMetadata,
  } from '$journey/journey.interfaces';

  export const style: z.infer<typeof styleSchema> = {};

  export let callback: SelectIdPCallback;
  export let callbackMetadata: Maybe<CallbackMetadata>;
  export let selfSubmitFunction: Maybe<SelfSubmitFunction> = null;
  export let stepMetadata: Maybe<StepMetadata>;

  let idps: { value: string; text: string }[];

  /**
   * @function setButtonValue - Sets the value on the callback on button click
   * @param {number} index
   */
  function setBtnValue(value: string) {
    callback.setProvider(value);
    if (callbackMetadata) {
      callbackMetadata.derived.isReadyForSubmission = true;
    }
    selfSubmitFunction && selfSubmitFunction();
  }

  $: {
    const localAuthentication = callback
      .getProviders()
      .filter((provider) => provider.provider === 'localAuthentication');

    const socialProviders = callback
      .getProviders()
      .filter((provider) => provider.provider !== 'localAuthentication');
    /**
     * If local authentication is available and the user hasn't selected a provider yet,
     * assume that clicking "next" will indicate the user wants to use local authentication.
     * Only set this default when the callback has no value; otherwise this can overwrite a
     * social-provider selection during re-renders.
     */
    if (localAuthentication.length > 0 && !callback.getInputValue()) {
      callback.setProvider('localAuthentication');
    }

    idps = socialProviders.map((option) => ({
      value: option.provider,
      text: option.uiConfig.buttonDisplayName,
    }));
  }
</script>

{#each idps as idp}
  <Grid num={1}>
    {#if idp.text.toUpperCase().includes('APPLE')}
      <Button
        classes="tw_button-apple dark:tw_button-apple_dark"
        type="button"
        width="auto"
        onClick={() => setBtnValue(idp.value)}
      >
        <AppleIcon classes="tw_inline-block tw_fill-current" />
        <T key="continueWith" />
        {idp.text}
      </Button>
    {:else if idp.text.toUpperCase().includes('FACEBOOK')}
      <Button
        classes="tw_button-facebook dark:tw_button-facebook_dark"
        type="button"
        width="auto"
        onClick={() => setBtnValue(idp.value)}
      >
        <FacebookIcon classes="tw_inline-block tw_fill-current" />
        <T key="continueWith" />
        {idp.text}
      </Button>
    {:else if idp.text.toUpperCase().includes('GOOGLE')}
      <Button
        classes="tw_button-google dark:tw_button-google_dark"
        type="button"
        width="auto"
        onClick={() => setBtnValue(idp.value)}
      >
        <GoogleIcon classes="tw_inline-block tw_fill-current" />
        <T key="continueWith" />
        {idp.text}
      </Button>
    {:else if idp.text.toUpperCase().includes('LINKEDIN')}
      <Button
        classes="tw_button-linkedin dark:tw_button-linkedin_dark"
        type="button"
        width="auto"
        onClick={() => setBtnValue(idp.value)}
      >
        <LinkedinIcon classes="tw_inline-block tw_fill-current" />
        <T key="continueWith" />
        {idp.text}
      </Button>
    {:else if idp.text.toUpperCase().includes('MICROSOFT')}
      <Button
        classes="tw_button-microsoft dark:tw_button-microsoft_dark"
        type="button"
        width="auto"
        onClick={() => setBtnValue(idp.value)}
      >
        <MicrosoftIcon classes="tw_inline-block tw_fill-current" />
        <T key="continueWith" />
        {idp.text}
      </Button>
    {/if}
  </Grid>
{/each}

{#if stepMetadata && stepMetadata.derived.numOfCallbacks > 1}
  <Grid num={1}>
    <hr class="tw_border-0 tw_border-b tw_border-secondary-light dark:tw_border-secondary-dark" />
  </Grid>
{/if}
